import * as React from 'react';
import { useContext } from 'react';
import styled from 'styled-components';
import { centered } from '@/components/use-shared-element';
import { GradientModal } from '@/components/gradient-modal';
import {
  WelcomeModalContext,
  WelcomeModalStep,
} from '@/features/welcome-quiz/shared/context';

export const WELCOME_MODAL_RECT = centered(65, 80);
const WELCOME_MODAL_RECT_MOBILE = centered(30, 80);

export interface WelcomeModalProps {
  fromRef: React.MutableRefObject<HTMLElement>;
  header?: any;
  footer?: any;
  children?: any;
  isMobileWidth?: boolean;
}

export function BaseWelcomeModal({
  fromRef,
  header,
  footer,
  children,
  isMobileWidth,
}: WelcomeModalProps) {
  const { isVisible, setIsVisible, currentStep } =
    useContext(WelcomeModalContext);

  return (
    <GradientModal
      closeTop="0.5rem"
      closeRight="2.5rem"
      fromStyle={{
        borderRadius: `10rem`,
        background: `white`,
      }}
      toStyle={{
        borderRadius: `3rem`,
        background: `white`,
      }}
      isVisible={isVisible}
      showBackdrop
      shadowFixScrollerMargin={0}
      backdropBlur={13}
      overlayBoxShadow="0 25px 60px rgba(0, 0, 0, 0.14), 0 8px 20px rgba(0, 0, 0, 0.9)"
      targetPosAndSize={
        isMobileWidth ? WELCOME_MODAL_RECT_MOBILE : WELCOME_MODAL_RECT
      }
      fromRef={fromRef}
      overflowTop={{ heightInRem: 0 }}
      overflowBottom={{ heightInRem: 0 }}
      hideCloseButton={currentStep === WelcomeModalStep.DONE}
      disableCloseOnBackdropClick={currentStep === WelcomeModalStep.DONE}
      onClose={() => setIsVisible(false)}
      headerTop={1}
      header={header || ``}
      footer={footer ? <FooterWrapper>{footer}</FooterWrapper> : ``}
    >
      <AnimatedBackground />
      <ContentWrapper>{children !== undefined && children}</ContentWrapper>
    </GradientModal>
  );
}

const AnimatedBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    filter: blur(60px);
    opacity: 0.6;
  }

  &::before {
    width: 600px;
    height: 200px;
    background: #ebd7a2;
    bottom: -150px;
    left: -50px;
    animation: float1 8s ease-in-out infinite;
  }

  &::after {
    width: 350px;
    height: 150px;
    background: #e7e5f9;
    bottom: -130px;
    right: -30px;
    animation: float2 12s ease-in-out infinite;
  }

  @keyframes float1 {
    0%,
    100% {
      transform: translate(0, 0);
    }
    25% {
      transform: translate(60px, -80px);
    }
    50% {
      transform: translate(-40px, -120px);
    }
    75% {
      transform: translate(80px, -60px);
    }
  }

  @keyframes float2 {
    0%,
    100% {
      transform: translate(0, 0);
    }
    20% {
      transform: translate(-80px, -100px);
    }
    40% {
      transform: translate(40px, -140px);
    }
    60% {
      transform: translate(-60px, -80px);
    }
    80% {
      transform: translate(60px, -120px);
    }
  }
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 3rem;
  padding: 0;
  position: relative;
  z-index: 1;
`;

const FooterWrapper = styled.div`
  margin-bottom: 0.5rem;
  position: relative;
  z-index: 1;
`;
